import React, { useEffect, useRef, useState, ReactNode } from 'react';
import './StickyScrollSection.css';

interface StickyScrollSectionProps {
  children: ReactNode;
  settings?: {
    triggerPoint?: number; // Percentage of viewport height where sticky effect starts
    duration?: number; // Duration for each section as ratio of viewport height
    sectionHeight?: number; // Height of each section in vh units for display
    disableMobile?: boolean; // Whether to disable sticky effect on mobile
    mobileBreakpoint?: number; // Mobile breakpoint in pixels
    scrollIndicator?: boolean; // Whether to show scroll indicators
    smoothScroll?: boolean; // Whether to use smooth scrolling
    bottomOffset?: number; // Offset from bottom in pixels
    onSectionChange?: (section: number, progress: number) => void; // Callback for section changes
  };
  containerRef?: React.RefObject<HTMLDivElement | null>; // Reference to the sticky scroll element
}

export const StickyScrollSection = ({
  children,
  settings = {},
  containerRef = useRef<HTMLDivElement>(null),
}: StickyScrollSectionProps) => {
  // Element references
  const stickyRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  
  // State variables
  const [isSticky, setIsSticky] = useState(false);
  const [transitionProgress, setTransitionProgress] = useState(0); // 0 = normal, 1 = fully sticky
  const [sectionCount, setSectionCount] = useState(0);
  const [activeSection, setActiveSection] = useState(0);
  const [progress, setProgress] = useState(0);
  const [totalHeight, setTotalHeight] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  // Default settings
  const {
    triggerPoint = 5, // Very early trigger for gradual transition
    duration = 1.2, // Each section takes 1.2x viewport height
    sectionHeight = 100, // Each section displays at 100vh height
    disableMobile = false, // Default enabled on mobile
    mobileBreakpoint = 768, 
    scrollIndicator = true,
    smoothScroll = true,
    bottomOffset = 0,
    onSectionChange
  } = settings;

  // Detect mobile device based on breakpoint
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < mobileBreakpoint);
    };
    
    window.addEventListener('resize', checkMobile);
    checkMobile();
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, [mobileBreakpoint]);
  
  // Initialize sections and calculate container height
  useEffect(() => {
    if (!sectionsRef.current) return;
    
    // Get all direct children (sections)
    const sectionElements = Array.from(sectionsRef.current.children) as HTMLDivElement[];
    setSectionCount(sectionElements.length);
    
    // Store references to each section
    sectionElements.forEach((section, index) => {
      sectionRefs.current.set(index, section);
    });
    
    // Calculate total height needed for scrolling
    // Viewport height * number of sections * duration factor + any offset
    const calculatedHeight = (
      window.innerHeight * 
      sectionElements.length * 
      duration
    ) + bottomOffset;
    
    setTotalHeight(calculatedHeight);
  }, [children, duration, bottomOffset]);
  
  // Handle scroll events with seamless sticky transition
  useEffect(() => {
    // Skip if disabled on mobile
    if (isMobile && disableMobile) return;
    if (!containerRef.current) return;
    
    let isScrolling = false;
    
    const handleScroll = () => {
      if (!isScrolling) {
        window.requestAnimationFrame(() => {
          const container = containerRef.current;
          if (!container || !stickyRef.current) {
            isScrolling = false;
            return;
          }
          
          const rect = container.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const triggerDistance = viewportHeight * triggerPoint / 100;
          
          // Calculate transition progress for smooth entry
          const preTransitionZone = viewportHeight * 0.3; // 30vh before trigger
          const transitionStart = triggerDistance + preTransitionZone;
          
          let newTransitionProgress = 0;
          
          if (rect.top <= transitionStart) {
            if (rect.top >= triggerDistance) {
              // Pre-transition zone - prepare for sticky
              newTransitionProgress = (transitionStart - rect.top) / preTransitionZone;
              newTransitionProgress = Math.max(0, Math.min(1, newTransitionProgress));
            } else {
              // Fully sticky
              newTransitionProgress = 1;
            }
          }
          
          setTransitionProgress(newTransitionProgress);
          
          // Calculate how far through the container we've scrolled (0-1)
          const scrollableDistance = rect.height - viewportHeight;
          const scrollTop = Math.max(0, -rect.top);
          const scrollProgress = Math.min(1, scrollTop / scrollableDistance);
          
          if (rect.top <= triggerDistance) {
            // We've reached the trigger point - activate sticky mode
            
            // Check if we're at the bottom of the container
            if (rect.bottom <= viewportHeight + bottomOffset) {
              // Exit sticky mode at the bottom
              setIsSticky(false);
              setTransitionProgress(0);
              
              // Position sticky content at the bottom of container
              if (stickyRef.current) {
                stickyRef.current.style.position = 'absolute';
                stickyRef.current.style.bottom = '0';
                stickyRef.current.style.top = 'auto';
                stickyRef.current.style.transform = 'translate3d(0, 0, 0)';
                stickyRef.current.style.opacity = '1';
              }
            } else {
              // We're in sticky mode - use natural sticky positioning
              setIsSticky(true);
              
              if (stickyRef.current) {
                // Use sticky positioning for natural flow, then switch to fixed
                stickyRef.current.style.position = 'sticky';
                stickyRef.current.style.top = '0';
                stickyRef.current.style.bottom = 'auto';
                stickyRef.current.style.transform = 'translate3d(0, 0, 0)';
                stickyRef.current.style.opacity = '1';
                stickyRef.current.style.transition = 'none'; // Remove transitions to prevent jumping
              }
              
              // Calculate which section should be active based on scroll position
              if (sectionCount > 0) {
                // Calculate section transitions
                const sectionSize = 1 / sectionCount;
                
                // Calculate which section we should be in
                const rawSectionIndex = scrollProgress / sectionSize;
                
                // Use floor with offset for section switching
                const newActiveSection = Math.min(
                  Math.floor(rawSectionIndex + 0.3), 
                  sectionCount - 1
                );
                
                // Calculate progress within the current section (0-1)
                const sectionProgress = Math.max(0, Math.min(1, 
                  (scrollProgress - (newActiveSection * sectionSize)) / sectionSize
                ));
                
                // Only update if section actually changed
                if (newActiveSection !== activeSection) {
                  setActiveSection(newActiveSection);
                }
                setProgress(sectionProgress);
                
                // Trigger callback with section change
                if (onSectionChange) {
                  onSectionChange(newActiveSection, sectionProgress);
                }
              }
            }
          } else {
            // We're approaching the trigger point - prepare for transition
            setIsSticky(false);
            
            if (stickyRef.current) {
              // Use natural positioning without any scaling effects
              stickyRef.current.style.position = 'relative';
              stickyRef.current.style.top = '0';
              stickyRef.current.style.transform = 'translate3d(0, 0, 0)';
              stickyRef.current.style.opacity = '1';
              stickyRef.current.style.transition = 'none';
            }
            
            // Reset to first section
            setActiveSection(0);
            setProgress(0);
          }
          
          isScrolling = false;
        });
        isScrolling = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [triggerPoint, sectionCount, isMobile, disableMobile, bottomOffset, activeSection, onSectionChange]);
  
  // Update section visibility based on active section
  useEffect(() => {
    // Each section gets appropriate visibility and transforms
    sectionRefs.current.forEach((section, index) => {
      // Only show the active section, hide all others
      if (index === activeSection) {
        section.style.opacity = '1';
        section.style.visibility = 'visible';
        section.style.transform = 'translateY(0) translate3d(0, 0, 0)';
        section.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
        section.style.zIndex = '10';
      } else {
        section.style.opacity = '1';
        section.style.visibility = 'hidden';
        section.style.transform = 'translateY(0) translate3d(0, 0, 0)';
        section.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
        section.style.zIndex = '1';
      }
    });
  }, [activeSection, progress]);
  
  // Helper function to scroll to a specific section
  const scrollToSection = (index: number) => {
    if (!containerRef.current) return;
    
    const containerTop = containerRef.current.offsetTop;
    const viewportHeight = window.innerHeight;
    const sectionSize = 1 / sectionCount;
    const targetProgress = index * sectionSize + (sectionSize / 2); // Target middle of section
    
    const scrollPosition = containerTop + (targetProgress * (totalHeight - viewportHeight));
    
    window.scrollTo({
      top: scrollPosition,
      behavior: smoothScroll ? 'smooth' : 'auto'
    });
  };

  return (
    <div 
      className="sticky-scroll-section" 
      ref={containerRef}
      style={{ height: `${totalHeight}px` }}
      data-is-sticky={isSticky ? 'true' : 'false'}
      data-active-section={activeSection}
    >
      <div 
        className={`sticky-scroll-content ${isSticky ? 'is-sticky' : ''}`}
        ref={stickyRef}
      >
        <div 
          className="sticky-scroll-sections"
          ref={sectionsRef}
        >
          {React.Children.map(children, (child, index) => (
            <div 
              className={`sticky-scroll-section-content ${index === activeSection ? 'active' : ''}`}
              style={{
                height: `${sectionHeight}vh`
              }}
              data-section-index={index}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      
      {/* Scroll indicators */}
      {scrollIndicator && isSticky && sectionCount > 1 && (
        <div className="sticky-scroll-indicators">
          {Array.from({ length: sectionCount }).map((_, index) => (
            <button
              key={index}
              className={`scroll-indicator ${index === activeSection ? 'active' : ''}`}
              onClick={() => scrollToSection(index)}
              aria-label={`Go to section ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StickyScrollSection;